<script setup lang="ts">
import { ref } from 'vue'
import { useDisplay } from 'vuetify'
import { DOCUMENT_EDITION_MAX_LENGTH } from '@/modules/documents-new/constants/documentStatuses'
import { DocumentsApi } from '@/modules/documents-new/services/documentsApi'
import { useFileUpload } from '@/modules/documents-new/composables'
import { useNotifications } from '@/modules/feedback/composables/useNotifications'
import DateMaskInput from '@/components/createdUi/DateMaskInput.vue'
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import FileDropLayer from '@/modules/files/components/FileDropLayer.vue'
import { useFormFileDrop } from '@/modules/files/composables/useFormFileDrop'

interface Props {
  isOpen: boolean
  documentId: string | null
  documentName?: string
  currentRevision?: string | null
}

interface Emits {
  (e: 'update:isOpen', value: boolean): void
  (e: 'added'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { smAndDown } = useDisplay()
const { showError, showSuccess } = useNotifications()

const { fileInput, fileInputRef, onFileSelect, openFileDialog, removeFile } = useFileUpload()

const formData = ref({
  edition: '',
  approved_at: '',
  valid_until: '',
  effective_at: '',
})
const isSubmitting = ref(false)
const formRef = ref()
const cardRef = ref<{ $el?: HTMLElement } | null>(null)

const formFileDrop = useFormFileDrop({
  getScope: () => {
    const el = cardRef.value?.$el
    return el instanceof HTMLElement ? el : null
  },
  isDisabled: () => !props.isOpen || isSubmitting.value,
  addFiles: files => {
    if (!files.length) {
      return
    }
    fileInput.value = files[0]
  },
})

const closeModal = () => {
  emit('update:isOpen', false)
  resetForm()
}

const resetForm = () => {
  formData.value = {
    edition: '',
    approved_at: '',
    valid_until: '',
    effective_at: '',
  }
  removeFile()
  formRef.value?.resetValidation()
}

const onSubmit = async () => {
  if (!props.documentId || !fileInput.value) return

  isSubmitting.value = true
  try {
    const fd = new FormData()
    if (formData.value.edition) fd.append('edition', formData.value.edition)
    if (formData.value.approved_at) fd.append('approved_at', formData.value.approved_at)
    if (formData.value.valid_until) fd.append('valid_until', formData.value.valid_until)
    if (formData.value.effective_at) fd.append('effective_at', formData.value.effective_at)
    fd.append('file', fileInput.value)

    await DocumentsApi.addDocumentVersion(props.documentId, fd)
    showSuccess('Редакция документа успешно добавлена')
    emit('added')
    closeModal()
  } catch (e: unknown) {
    console.error('Ошибка при добавлении редакции:', e)
    showError(e, 'Добавление редакции документа')
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <VDialog
    :model-value="isOpen"
    :max-width="smAndDown ? '100%' : 600"
    :fullscreen="smAndDown"
    @update:model-value="emit('update:isOpen', $event)"
  >
    <FileDropLayer :rect="formFileDrop.rect.value" />
    <VCard
      ref="cardRef"
      :class="smAndDown ? 'pa-4' : 'pa-6'"
    >
      <div class="d-flex justify-space-between align-center mb-4">
        <span class="text-h5 font-weight-bold">Добавление новой редакции документа</span>
        <CustomCloseButton :close-action="closeModal" />
      </div>

      <div class="revision-context text-body-2 text-medium-emphasis mb-6">
        <VIcon
          icon="ri-links-line"
          size="16"
          class="me-1"
        />
        <span>Новая редакция документа для:</span>
        <span class="revision-context__details">
          {{ documentName || 'документ' }}
          <template v-if="currentRevision">
            / Текущая редакция: {{ currentRevision }}
          </template>
        </span>
      </div>

      <VForm
        ref="formRef"
        class="revision-form d-flex flex-column gap-4"
        @submit.prevent="onSubmit"
      >
        <VTextField
          v-model="formData.edition"
          label="Редакция"
          density="compact"
          variant="outlined"
          :maxlength="DOCUMENT_EDITION_MAX_LENGTH"
          hide-details
        />

        <DateMaskInput
          v-model="formData.approved_at"
          density="compact"
        >
          <template #label>
            <span>Дата утверждения</span>
          </template>
        </DateMaskInput>

        <div class="form-row-two-cols">
          <DateMaskInput
            v-model="formData.effective_at"
            density="compact"
          >
            <template #label>
              <span>Дата ввода</span>
            </template>
          </DateMaskInput>
          <DateMaskInput
            v-model="formData.valid_until"
            density="compact"
          >
            <template #label>
              <span>Дата истечения</span>
            </template>
          </DateMaskInput>
        </div>

        <template v-if="!fileInput">
          <VBtn
            variant="outlined"
            color="primary"
            prepend-icon="ri-upload-2-line"
            class="text-none upload-file-btn"
            block
            @click="openFileDialog"
          >
            Загрузить файл
          </VBtn>
          <input
            ref="fileInputRef"
            type="file"
            style="display: none"
            @change="onFileSelect"
          >
        </template>
        <template v-else>
          <div class="d-flex align-center gap-2 pa-3 file-card">
            <span class="text-body-2 flex-grow-1 text-truncate">{{ fileInput.name }}</span>
            <VIconBtn
              icon="ri-close-line"
              variant="text"
              size="small"
              aria-label="Удалить файл"
              @click="removeFile"
            />
          </div>
          <input
            ref="fileInputRef"
            type="file"
            style="display: none"
            @change="onFileSelect"
          >
        </template>
      </VForm>

      <div class="d-flex justify-end mt-6">
        <VBtn
          color="primary"
          variant="elevated"
          class="text-none px-6"
          :loading="isSubmitting"
          :disabled="!fileInput"
          @click="onSubmit"
        >
          Добавить
        </VBtn>
      </div>
    </VCard>
  </VDialog>
</template>

<style lang="scss" scoped>
.revision-context {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}

.revision-context__details {
  color: rgba(var(--v-theme-on-surface), 0.6);
  min-inline-size: 0;
  max-inline-size: 100%;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.form-row-two-cols {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;

  @media (max-width: 600px) {
    grid-template-columns: minmax(0, 1fr);
  }

  :deep(.v-field__input) {
    align-items: center;
  }
}

.revision-form {
  :deep(.v-text-field.v-input.v-input--density-compact:not(.v-textarea) .v-field__input),
  :deep(.v-autocomplete.v-input.v-input--density-compact:not(.v-textarea) .v-field__input),
  :deep(.v-combobox.v-input.v-input--density-compact:not(.v-textarea) .v-field__input),
  :deep(.v-select.v-input.v-input--density-compact:not(.v-textarea) .v-field__input),
  :deep(.date-mask-input .v-field__input) {
    min-block-size: 56px;
    min-height: 56px;
    padding-top: 8px;
    padding-bottom: 8px;
  }

  :deep(.v-field) {
    font-size: 14px;
  }
}

.upload-file-btn {
  block-size: 56px;
  min-block-size: 56px;
}

.file-card {
  background: rgba(var(--v-theme-primary), 0.08);
  border-radius: 8px;
  border: 1px solid rgba(var(--v-theme-primary), 0.12);
}
</style>
